import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import Uploader from "@/components/Uploader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";

const field = "w-full border border-neutral-200 px-3 py-2.5 text-sm focus:border-neutral-900 outline-none";
const CAT_TYPES = [{ v: "clothing", l: "Vêtements" }, { v: "jewelry", l: "Bijoux" }, { v: "decoration", l: "Décoration & Objets" }, { v: "cosmetics", l: "Cosmétiques & Beauté" }];
const DIMS = [{ k: "size", l: "Taille / Format" }, { k: "color", l: "Couleur" }, { k: "shade", l: "Teinte" }];
const BADGES = ["Nouveauté", "Pièce unique", "Stock limité", "Coup de cœur", "Sélection", "Exclusivité", "Best-seller", "Vegan", "Naturel", "Bio", "Édition limitée"];

export default function ProductForm({ initial, onSaved, onCancel }) {
  const [cats, setCats] = useState([]);
  const [schema, setSchema] = useState({});
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(() => initial || {
    name: "", category: "clothing", subcategory: "", description: "", price: 0, sale_price: null,
    images: [], badges: [], attributes: {}, variant_options: [], variants: [{ options: {}, stock: 0, price: null }],
    keywords: [], dimensions: "", prep_time: "2-4 jours", beauty: {},
  });

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data.categories)); }, []);
  useEffect(() => {
    api.get(`/attributes/${f.category}`).then(({ data }) => { setSchema(data.schema); setOptions(data.options); });
  }, [f.category]);

  const subList = cats.find((c) => c.type === f.category)?.subcategories || [];
  const attrKeys = Object.keys(schema).filter((k) => k !== "subcategory");

  const toggleAttr = (key, val) => {
    const cur = f.attributes[key] || [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setF({ ...f, attributes: { ...f.attributes, [key]: next } });
  };
  const toggleDim = (k) => {
    const has = f.variant_options.includes(k);
    setF({ ...f, variant_options: has ? f.variant_options.filter((x) => x !== k) : [...f.variant_options, k] });
  };
  const setVariant = (i, patch) => setF({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) });
  const addVariant = () => setF({ ...f, variants: [...f.variants, { options: {}, stock: 0, price: null }] });
  const rmVariant = (i) => setF({ ...f, variants: f.variants.filter((_, idx) => idx !== i) });

  const save = async (submit) => {
    setLoading(true);
    try {
      const payload = { ...f, price: Number(f.price) || 0, sale_price: f.sale_price ? Number(f.sale_price) : null };
      let pid = initial?.id;
      if (initial?.id) { await api.put(`/seller/products/${initial.id}`, payload); }
      else { const { data } = await api.post("/seller/products", payload); pid = data.product.id; }
      if (submit && pid) await api.post(`/seller/products/${pid}/submit`);
      toast.success(submit ? "Produit soumis à validation" : "Produit enregistré");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="over text-[0.58rem] text-neutral-500">Photos *</label>
        <div className="mt-2"><Uploader value={f.images} onChange={(v) => setF({ ...f, images: v })} hint="Photos de qualité, fond neutre recommandé. La première photo est la principale." /></div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="over text-[0.58rem] text-neutral-500">Nom *</label><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="pf-name" /></div>
        <div>
          <label className="over text-[0.58rem] text-neutral-500">Catégorie *</label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v, subcategory: "", attributes: {} })}>
            <SelectTrigger className="rounded-none text-sm" data-testid="pf-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CAT_TYPES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="over text-[0.58rem] text-neutral-500">Sous-catégorie</label>
          <Select value={f.subcategory} onValueChange={(v) => setF({ ...f, subcategory: v })}>
            <SelectTrigger className="rounded-none text-sm" data-testid="pf-subcategory"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{subList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="over text-[0.58rem] text-neutral-500">Dimensions (optionnel)</label><input value={f.dimensions || ""} onChange={(e) => setF({ ...f, dimensions: e.target.value })} className={field} /></div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="over text-[0.58rem] text-neutral-500">Prix (€) *</label><input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={field} data-testid="pf-price" /></div>
        <div><label className="over text-[0.58rem] text-neutral-500">Prix promo (€)</label><input type="number" value={f.sale_price || ""} onChange={(e) => setF({ ...f, sale_price: e.target.value })} className={field} /></div>
      </div>

      <div><label className="over text-[0.58rem] text-neutral-500">Description</label><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${field} h-24`} data-testid="pf-description" /></div>

      {/* Dynamic attributes */}
      {attrKeys.map((key) => (
        <div key={key}>
          <label className="over text-[0.58rem] text-neutral-500">{schema[key]}</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(options[key] || []).map((o) => (
              <button type="button" key={o} onClick={() => toggleAttr(key, o)}
                className={`text-xs px-3 py-1.5 border ${(f.attributes[key] || []).includes(o) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-neutral-200"}`}>{o}</button>
            ))}
          </div>
        </div>
      ))}

      {/* Beauty fields */}
      {f.category === "cosmetics" && (
        <div className="bg-[#FAFAFA] p-4 space-y-3">
          <span className="over text-[0.6rem]">Informations beauté</span>
          {[["ingredients", "Ingrédients"], ["inci", "Liste INCI"], ["usage", "Mode d'emploi"], ["precautions", "Précautions"], ["pao", "PAO (ex: 12M)"], ["country", "Pays de fabrication"], ["allergens", "Allergènes"]].map(([k, l]) => (
            <input key={k} placeholder={l} value={f.beauty?.[k] || ""} onChange={(e) => setF({ ...f, beauty: { ...f.beauty, [k]: e.target.value } })} className={field} />
          ))}
        </div>
      )}

      {/* Variants */}
      <div>
        <label className="over text-[0.58rem] text-neutral-500">Dimensions de variante</label>
        <div className="flex gap-2 mt-2">
          {DIMS.map((d) => (
            <button type="button" key={d.k} onClick={() => toggleDim(d.k)} className={`text-xs px-3 py-1.5 border ${f.variant_options.includes(d.k) ? "bg-[#E0115F] text-white border-[#E0115F]" : "border-neutral-200"}`}>{d.l}</button>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          {f.variants.map((v, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 border border-neutral-100 p-2" data-testid={`pf-variant-${i}`}>
              {f.variant_options.length === 0 && <span className="text-xs text-neutral-400 px-2">Variante unique</span>}
              {f.variant_options.map((dim) => (
                <input key={dim} placeholder={DIMS.find((d) => d.k === dim)?.l} value={v.options?.[dim] || ""}
                  onChange={(e) => setVariant(i, { options: { ...v.options, [dim]: e.target.value } })}
                  className="border border-neutral-200 px-2 py-1.5 text-sm w-32" />
              ))}
              <input type="number" placeholder="Stock" value={v.stock} onChange={(e) => setVariant(i, { stock: Number(e.target.value) })} className="border border-neutral-200 px-2 py-1.5 text-sm w-24" data-testid={`pf-variant-stock-${i}`} />
              <input type="number" placeholder="Prix spécifique" value={v.price || ""} onChange={(e) => setVariant(i, { price: e.target.value ? Number(e.target.value) : null })} className="border border-neutral-200 px-2 py-1.5 text-sm w-32" />
              {f.variants.length > 1 && <button type="button" onClick={() => rmVariant(i)} className="text-neutral-400 hover:text-[#E0115F]"><Trash2 size={15} /></button>}
            </div>
          ))}
          {f.variant_options.length > 0 && <button type="button" onClick={addVariant} className="over text-[0.58rem] inline-flex items-center gap-1 mt-1"><Plus size={13} /> Ajouter une variante</button>}
        </div>
      </div>

      {/* Badges */}
      <div>
        <label className="over text-[0.58rem] text-neutral-500">Badges</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {BADGES.map((b) => (
            <button type="button" key={b} onClick={() => setF({ ...f, badges: f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b] })}
              className={`over text-[0.55rem] px-2.5 py-1 border ${f.badges.includes(b) ? "border-[#D4AF37] bg-[#D4AF37] text-white" : "border-neutral-200"}`}>{b}</button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-neutral-100">
        <button disabled={loading} onClick={() => save(false)} className="btn btn-outline" data-testid="pf-save-draft">Enregistrer en brouillon</button>
        <button disabled={loading} onClick={() => save(true)} className="btn btn-magenta" data-testid="pf-submit">Soumettre à validation</button>
        <button onClick={onCancel} className="over text-[0.62rem] ml-auto">Annuler</button>
      </div>
    </div>
  );
}
